import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    TextField,
    useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRemindMeContext } from '../../context/RemindMeContext';
import { useDeviceId } from '../../hooks/useDeviceId';
import { useAlert } from '../../context/AlertContext';
import { companyId } from '../../utils/remindMeServices/RemindMeAPIServices';
import { sendEmailText } from '../../utils/remindMeServices/TextToMail';
import CommonLoader from '../../components/remindMe/CommonLoader';
import BottomSendContainer from '../../components/remindMe/BottomSendContainer';
import NoEmailDialog from '../../components/remindMe/NoEmailDialog';
import InnerTopBar from '../../components/layout/InnerTopBar';


const SendTextScreen: React.FC = () => {
    const { selectedEmail } = useRemindMeContext();
    const [title, setTitle] = useState('');
    const [note, setNote] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSendButtonEnabled, setIsSendButtonEnabled] = useState<boolean>(false);
    const { deviceId } = useDeviceId();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const theme = useTheme();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!selectedEmail) {
            setIsDialogOpen(true);
        }
    }, [selectedEmail]);

    useEffect(() => {
        setIsSendButtonEnabled(note.trim() !== '');
    }, [note]);

    const isIphone = useMemo(() => {
        const userAgent = navigator.userAgent || navigator.vendor;
        return /iPhone|iPad/i.test(userAgent);
    }, []);

    const handleSend = async (selectedEmail: string) => {
        if (selectedEmail.length <= 0 && note.length <= 0) {
            showAlert('Please ensure an email is selected and a note is entered.', 'warning');
            return;
        }
        console.log(deviceId);
        if (!deviceId) {
            return;
        }
        const companyIdNumber = companyId ? Number(companyId) : NaN;

        try {
            setIsLoading(true);
            await sendEmailText(
                selectedEmail,
                note,
                'text',
                companyIdNumber,
                deviceId
            );
            showAlert(`Success! Email sent to ${selectedEmail}`, 'success');

            navigate('/home');
        } catch (error) {
            console.log(error);
            showAlert('Oops! Sending email failed. Please check your data connection.', 'error');

        }
        finally {
            setIsLoading(false)
        }
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        navigate('/settings');
    };

    return (
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center", flex: 1,
        }}>
            <InnerTopBar title="Send Text" />
            {isLoading ?
                <CommonLoader isVisible={isLoading} /> :
                < Box sx={{ flexGrow: 1, p: 1 }}>
                    <TextField
                        fullWidth
                        placeholder="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        variant="standard"
                        sx={{
                            borderRadius: "8px",
                            bgcolor: theme.palette.background.paper,
                            mb: 2,
                            '& .MuiInput-root': {
                                fontSize: '22px',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: "8px",
                                '&::before, &::after': {
                                    display: 'none'
                                },
                                '& input': {
                                    padding: '8px',
                                },
                                '& input::placeholder': {
                                    color: theme.palette.text.disabled,
                                    opacity: 1
                                },
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        placeholder="Note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        variant="standard"
                        sx={{
                            borderRadius: "8px",
                            bgcolor: theme.palette.background.paper,
                            '& .MuiInput-root': {
                                fontSize: '18px',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: "8px",
                                alignItems: 'flex-start',
                                '&::before, &::after': {
                                    display: 'none', // <-- add this line to remove underline
                                },
                                '& textarea': {
                                    height: '300px',
                                    padding: '8px',
                                    boxSizing: 'border-box',
                                    resize: 'vertical',
                                },
                                '& textarea::placeholder': {
                                    color: theme.palette.text.disabled,
                                    opacity: 1,
                                }
                            }
                        }}
                    />
                </Box>}

            {/* Bottom Section */}
            <BottomSendContainer
                handleSend={handleSend}
                isSendEnabled={isSendButtonEnabled}
            />

            {/* Dialog */}
            <NoEmailDialog isDialogOpen={isDialogOpen} handleCloseDialog={handleCloseDialog} />

        </Box >
    );
};

export default SendTextScreen;

